import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Plus, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const COLS = ['no', 'date', 'purpose', 'origin', 'destination', 'km', 'odometer', 'driver', 'signature'];
const LABELS = ['title', 'start_label', 'end_label', 'total_label', 'page_total_label', 'carried_label'];

const RatesEditor = ({ rates, setRates }) => {
  const { t } = useTranslation();
  return (
    <div className="space-y-2">
      <p className="text-sm font-medium">{t('mileage.settings.rates')}</p>
      {rates.map((r, i) => (
        <div key={r.key || i} className="flex gap-2">
          <Input className="h-8 text-sm" value={r.label} onChange={(e) => setRates(rates.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))} data-testid={`mileage-rate-label-${i}`} />
          <Input type="number" step="0.01" className="h-8 w-28 text-sm" value={r.rate} onChange={(e) => setRates(rates.map((x, j) => (j === i ? { ...x, rate: e.target.value } : x)))} data-testid={`mileage-rate-value-${i}`} />
          <Button size="icon" variant="ghost" className="h-8 w-8 text-red-600" onClick={() => setRates(rates.filter((_, j) => j !== i))} data-testid={`mileage-rate-remove-${i}`}><Trash2 className="h-4 w-4" /></Button>
        </div>
      ))}
      <Button size="sm" variant="outline" onClick={() => setRates([...rates, { key: `r${Date.now()}`, label: '', rate: '' }])} data-testid="mileage-rate-add"><Plus className="h-4 w-4 mr-1" />{t('mileage.settings.addRate')}</Button>
    </div>
  );
};

/** Admin: statutory rates and the printed mileage-sheet template (title, captions, notes). */
export const MileageTemplateSettings = () => {
  const { t } = useTranslation();
  const [cfg, setCfg] = useState(null);
  useEffect(() => { axios.get(`${API}/mileage-sheets/config`).then((r) => setCfg(r.data)).catch(() => {}); }, []);
  if (!cfg) return null;
  const tpl = cfg.template;
  const setTpl = (k, v) => setCfg({ ...cfg, template: { ...tpl, [k]: v } });
  const save = async () => {
    try {
      const r = await axios.put(`${API}/mileage-sheets/config`, { rates: cfg.rates.map((x) => ({ ...x, rate: Number(x.rate) })), template: tpl });
      setCfg(r.data); toast.success(t('mileage.saved'));
    } catch (e) { toast.error(e.response?.data?.detail || t('mileage.failed')); }
  };
  return (
    <Card data-testid="mileage-template-settings">
      <CardHeader><CardTitle className="text-base">{t('mileage.settings.title')}</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        <RatesEditor rates={cfg.rates} setRates={(rates) => setCfg({ ...cfg, rates })} />
        <div className="grid gap-2 sm:grid-cols-2">
          {LABELS.map((k) => (
            <label key={k} className="text-xs text-slate-600">{t(`mileage.settings.${k === 'title' ? 'titleField' : k}`)}
              <Input className="h-8 text-sm" value={tpl[k] || ''} onChange={(e) => setTpl(k, e.target.value)} data-testid={`mileage-tpl-${k}`} />
            </label>
          ))}
        </div>
        <div>
          <p className="text-sm font-medium mb-1">{t('mileage.settings.columns')}</p>
          <div className="grid gap-2 sm:grid-cols-3">
            {COLS.map((k) => (
              <Input key={k} className="h-8 text-sm" value={tpl.columns[k] || ''} onChange={(e) => setTpl('columns', { ...tpl.columns, [k]: e.target.value })} data-testid={`mileage-tpl-col-${k}`} />
            ))}
          </div>
        </div>
        <div className="grid gap-2 sm:grid-cols-2">
          <label className="text-xs text-slate-600">{t('mileage.settings.rows_per_page')}
            <Input type="number" min="5" max="40" className="h-8 text-sm" value={tpl.rows_per_page} onChange={(e) => setTpl('rows_per_page', e.target.value)} data-testid="mileage-tpl-rows-per-page" />
          </label>
          <label className="flex items-center gap-2 text-sm mt-5">
            <Checkbox checked={!!tpl.show_company_line} onCheckedChange={(v) => setTpl('show_company_line', !!v)} data-testid="mileage-tpl-company-line" />
            {t('mileage.settings.show_company_line')}
          </label>
        </div>
        <Textarea rows={3} placeholder={t('mileage.settings.footer_note')} value={tpl.footer_note || ''} onChange={(e) => setTpl('footer_note', e.target.value)} data-testid="mileage-tpl-footer-note" />
        <Button onClick={save} data-testid="mileage-settings-save">{t('common.save')}</Button>
      </CardContent>
    </Card>
  );
};

export default MileageTemplateSettings;
